import axios from 'axios';
import authApi from './authApi';

// API base URL (will work in both development and production)
const API_URL = process.env.NODE_ENV === 'production' 
  ? '/api' // In production, use relative path to the API
  : process.env.REACT_APP_API_URL || 'http://localhost:5002/api'; // In development, use the proxy or explicit URL

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use(async (config) => {
  try {
    const token = authApi.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  } catch (error) {
    console.error('Error getting auth token:', error);
    return config;
  }
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Handle 401 errors by logging out
    if (error.response && error.response.status === 401) {
      authApi.logout();
      // Optionally redirect to login
      // window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  getUserData: async () => {
    const response = await api.get('/auth/user');
    return response.data;
  },
  updateUserRole: async (uid, role) => {
    const response = await api.put(`/auth/user/${uid}/role`, { role });
    return response.data;
  }
};

// Books API
export const booksAPI = {
  getAllBooks: async () => {
    const response = await api.get('/books');
    return response.data;
  },
  getBookById: async (bookId) => {
    const response = await api.get(`/books/${bookId}`);
    return response.data;
  },
  addBook: async (bookData) => {
    const response = await api.post('/books', bookData);
    return response.data;
  },
  updateBook: async (id, bookData) => {
    const response = await api.put(`/books/${id}`, bookData);
    return response.data;
  },
  deleteBook: async (id) => {
    const response = await api.delete(`/books/${id}`);
    return response.data;
  },
  markAsRead: async (bookId, readData) => {
    const response = await api.post(`/books/${bookId}/read`, readData);
    return response.data;
  },
  getUserBooks: async () => {
    const response = await api.get('/books/user');
    return response.data;
  }
};

// Chat API
export const chatAPI = {
  getChatHistory: async (bookId) => {
    const response = await api.get(`/chat/history/${bookId}`);
    return response.data;
  },
  sendBookMessage: async (bookId, message) => {
    const response = await api.post(`/chat/book/${bookId}`, { message });
    return response.data;
  },
  sendCharacterMessage: async (bookId, characterId, message) => {
    const response = await api.post(`/chat/character/${bookId}/${characterId}`, { message });
    return response.data;
  },
  sendMessage: async (bookId, message) => {
    const response = await api.post(`/chat/message`, { bookId, message });
    return response.data;
  },
  getBookContext: async (bookId) => {
    const response = await api.get(`/chat/context/${bookId}`);
    return response.data;
  }
};

// Review API
export const reviewAPI = {
  getReviews: async (bookId) => {
    const response = await api.get(`/reviews/${bookId}`);
    return response.data;
  },
  addReview: async (bookId, data) => {
    const response = await api.post(`/reviews/${bookId}`, data);
    return response.data;
  },
  editReview: async (reviewId, data) => {
    const response = await api.put(`/reviews/${reviewId}`, data);
    return response.data;
  },
  deleteReview: async (reviewId) => {
    const response = await api.delete(`/reviews/${reviewId}`);
    return response.data;
  },
};

export default api; 